// src/services/api/customerAPI.js
import { createAuthHeaders } from '../../utils/authUtils';

const FIREBASE_FUNCTIONS_BASE_URL = process.env.REACT_APP_FIREBASE_FUNCTIONS_BASE_URL;

/**
 * Customer API - handles all customer-related operations through Firebase Functions
 * Follows the same pattern as AppointmentAPI for consistency
 */
class CustomerAPI {
  /**
   * Retry configuration
   */
  static MAX_RETRIES = 2;
  static RETRY_DELAY = 1000; // 1 second

  /**
   * Sleep utility for retry delays
   */
  static _sleep(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Retry wrapper for API requests
   */
  static async _retryRequest(requestFn, retries = this.MAX_RETRIES) {
    try {
      return await requestFn();
    } catch (error) {
      if (retries > 0 && this._isRetryableError(error)) {
        console.warn(`API request failed, retrying... (${this.MAX_RETRIES - retries + 1}/${this.MAX_RETRIES})`, {
          error: error.message
        });
        await this._sleep(this.RETRY_DELAY);
        return this._retryRequest(requestFn, retries - 1);
      }
      throw error;
    }
  }

  /**
   * Check if error is retryable
   */
  static _isRetryableError(error) {
    // Retry on network errors, timeouts, and 5xx server errors
    return error.name === 'TypeError' || // Network errors
      error.message.includes('timeout') ||
      error.message.includes('fetch') ||
      (error.status >= 500 && error.status < 600);
  }

  /**
   * Handle API error response
   */
  static async _handleAPIError(response, operation) {
    let errorMessage = `API Error: ${response.status} ${response.statusText}`;

    try {
      const errorData = await response.json();
      if (errorData.error) {
        errorMessage = errorData.error;
      }
    } catch (parseError) {
      // If we can't parse the error response, use the status text
      console.warn('Could not parse error response:', parseError);
    }

    const error = new Error(errorMessage);
    error.status = response.status;
    error.operation = operation;
    throw error;
  }

  /**
   * Get all customers with optional filtering and pagination
   * @param {Object} options - Query options
   * @param {number} options.limit - Maximum number of customers to return (1-1000, default: 100)
   * @param {number} options.offset - Number of customers to skip (default: 0)
   * @param {string} options.sortBy - Field to sort by (default: 'created_at')
   * @param {string} options.sortOrder - Sort order 'ASC' or 'DESC' (default: 'DESC')
   * @returns {Promise<Array>} Array of customers
   */
  static async getAll(options = {}) {
    try {
      console.log('API: Getting all customers', { options });

      // Validate options
      const {
        limit = 950,
        offset = 0,
        sortBy = 'created_at',
        sortOrder = 'DESC'
      } = options;

      // Validate parameters
      if (limit < 1 || limit > 1000) {
        throw new Error('Limit must be between 1 and 1000');
      }

      if (offset < 0) {
        throw new Error('Offset must be non-negative');
      }

      const allowedSortColumns = [
        'created_at', 'updated_at', 'full_name', 'phone', 'email'
      ];
      if (!allowedSortColumns.includes(sortBy)) {
        throw new Error(`Invalid sort column. Allowed: ${allowedSortColumns.join(', ')}`);
      }

      if (!['ASC', 'DESC'].includes(sortOrder.toUpperCase())) {
        throw new Error('Sort order must be ASC or DESC');
      }

      return await this._retryRequest(async () => {
        const url = new URL(`${FIREBASE_FUNCTIONS_BASE_URL}/customers`);
        url.searchParams.set('operation', 'getAll');
        url.searchParams.set('limit', limit.toString());
        url.searchParams.set('offset', offset.toString());
        url.searchParams.set('sortBy', sortBy);
        url.searchParams.set('sortOrder', sortOrder.toUpperCase());

        console.log('API: Making request to:', url.toString());

        const response = await fetch(url.toString(), {
          method: 'GET',
          headers: await createAuthHeaders(),
        });

        if (!response.ok) {
          await this._handleAPIError(response, 'getAll');
        }

        const result = await response.json();

        if (!result.success) {
          throw new Error(result.error || 'API request was not successful');
        }

        console.log('API: Successfully retrieved customers', {
          count: result.data?.length || 0,
          limit,
          offset,
          sortBy,
          sortOrder
        });

        return result.data || [];
      });

    } catch (error) {
      console.error('API Error in getAll:', {
        options,
        error: error.message,
        stack: error.stack
      });

      throw new Error(`Failed to get customers: ${error.message}`);
    }
  }

  static async getById(customerId) {
    try {
      console.log('API: Getting customer by ID', { customerId });

      // Validate parameters
      if (!customerId) {
        throw new Error('Customer ID is required');
      }

      // Validate UUID format
      const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
      if (!uuidRegex.test(customerId)) {
        throw new Error('Invalid customer ID format');
      }

      return await this._retryRequest(async () => {
        const url = new URL(`${FIREBASE_FUNCTIONS_BASE_URL}/customers`);
        url.searchParams.set('operation', 'getById');
        url.searchParams.set('customerId', customerId);

        console.log('API: Making request to:', url.toString());

        const response = await fetch(url.toString(), {
          method: 'GET',
          headers: await createAuthHeaders(),
        });

        if (!response.ok) {
          await this._handleAPIError(response, 'getById');
        }

        const result = await response.json();

        if (!result.success) {
          throw new Error(result.error || 'API request was not successful');
        }

        // Handle null result (customer not found)
        if (result.data === null) {
          console.log('API: Customer not found', { customerId });
          return null;
        }

        console.log('API: Successfully retrieved customer by ID', {
          customerId: result.data.id,
          fullName: result.data.full_name
        });

        return result.data;
      });

    } catch (error) {
      console.error('API Error in getById:', {
        customerId,
        error: error.message,
        stack: error.stack
      });

      throw new Error(`Failed to get customer by ID: ${error.message}`);
    }
  }

  /**
   * Get customer by phone number
   * @param {string} phone - Phone number (various formats accepted)
   * @returns {Promise<Object|null>} Customer data or null if not found
   */
  static async getByPhone(phone) {
    try {
      console.log('API: Getting customer by phone', {
        phone: phone ? phone.substring(0, 3) + '***' : 'undefined'
      });

      // Critical parameter validation
      if (!phone) {
        throw new Error('Phone number is required');
      }

      if (typeof phone !== 'string') {
        throw new Error('Phone number must be a string');
      }

      // Basic phone validation before API call
      const cleanPhone = phone.replace(/\D/g, '');
      if (cleanPhone.length < 9 || cleanPhone.length > 15) {
        throw new Error('Invalid phone number length');
      }

      return await this._retryRequest(async () => {
        const url = new URL(`${FIREBASE_FUNCTIONS_BASE_URL}/customers`);
        url.searchParams.set('operation', 'getByPhone');
        url.searchParams.set('phone', phone);

        console.log('API: Making GET request to get customer by phone:', url.toString());

        const response = await fetch(url.toString(), {
          method: 'GET',
          headers: await createAuthHeaders(),
        });

        if (!response.ok) {
          await this._handleAPIError(response, 'getByPhone');
        }

        const result = await response.json();

        if (!result.success) {
          throw new Error(result.error || 'API request was not successful');
        }

        // Handle case where customer is not found (null response)
        if (!result.data) {
          console.log('API: Customer not found by phone', {
            phone: phone.substring(0, 3) + '***'
          });
          return null;
        }

        // Validate response data structure
        if (!result.data.id) {
          throw new Error('Invalid response: missing customer ID');
        }

        console.log('API: Successfully retrieved customer by phone', {
          customerId: result.data.id,
          fullName: result.data.full_name,
          phone: result.data.phone ? result.data.phone.substring(0, 3) + '***' : 'undefined',
          email: result.data.email
        });

        return result.data;
      });

    } catch (error) {
      console.error('API Error in getByPhone customer:', {
        phone: phone ? phone.substring(0, 3) + '***' : 'undefined',
        error: error.message,
        stack: error.stack
      });

      // Re-throw with more context
      throw new Error(`Failed to get customer by phone: ${error.message}`);
    }
  }

  // Implementation for src/services/api/customerAPI.js
  // ADD this method after getByPhone

  /**
   * Get customer's appointment history
   * @param {string} customerId - Customer ID (UUID)
   * @param {number} limit - Maximum number of appointments to return (default: 0 for no limit, max: 200)
   * @returns {Promise<Array>} Array of customer appointments
   */
  static async getCustomerAppointments(customerId, limit = 0) {
    try {
      console.log('API: Getting customer appointments', {
        customerId: customerId ? customerId.substring(0, 8) + '***' : 'undefined',
        limit: limit === 0 ? 'unlimited' : limit
      });

      // Critical parameter validation
      if (!customerId) {
        throw new Error('Customer ID is required');
      }

      if (typeof customerId !== 'string') {
        throw new Error('Customer ID must be a string');
      }

      // Validate UUID format
      const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
      if (!uuidRegex.test(customerId)) {
        throw new Error('Invalid customer ID format');
      }

      // Validate limit (0 means no limit)
      if (typeof limit !== 'number' || limit < 0 || limit > 200) {
        throw new Error('Limit must be a number between 0 and 200 (0 means no limit)');
      }

      return await this._retryRequest(async () => {
        const url = new URL(`${FIREBASE_FUNCTIONS_BASE_URL}/customers`);
        url.searchParams.set('operation', 'getCustomerAppointments');
        url.searchParams.set('customerId', customerId);
        url.searchParams.set('limit', limit.toString());

        console.log('API: Making GET request to get customer appointments:', url.toString());

        const response = await fetch(url.toString(), {
          method: 'GET',
          headers: await createAuthHeaders(),
        });

        if (!response.ok) {
          await this._handleAPIError(response, 'getCustomerAppointments');
        }

        const result = await response.json();

        if (!result.success) {
          throw new Error(result.error || 'API request was not successful');
        }

        // Handle case where no appointments found (empty array response)
        if (!result.data) {
          console.log('API: No appointments found for customer', {
            customerId: customerId.substring(0, 8) + '***'
          });
          return [];
        }

        // Validate response data structure
        if (!Array.isArray(result.data)) {
          throw new Error('Invalid response: expected array of appointments');
        }

        console.log('API: Successfully retrieved customer appointments', {
          customerId: customerId.substring(0, 8) + '***',
          limit,
          count: result.data.length,
          customerName: result.data[0]?.customer_name || 'Unknown',
          hasUpcoming: result.data.some(apt => apt.date >= new Date().toISOString().split('T')[0]),
          appointmentDateRange: result.data.length > 0 ? {
            earliest: result.data[result.data.length - 1]?.date,
            latest: result.data[0]?.date
          } : null
        });

        return result.data;
      });

    } catch (error) {
      console.error('API Error in getCustomerAppointments:', {
        customerId: customerId ? customerId.substring(0, 8) + '***' : 'undefined',
        limit,
        error: error.message,
        stack: error.stack
      });

      // Re-throw with more context
      throw new Error(`Failed to get customer appointments: ${error.message}`);
    }
  }

  static async create(customerData) {
    try {
      console.log('API: Creating new customer', {
        fullName: customerData?.full_name,
        phone: customerData?.phone,
        email: customerData?.email
      });

      // Client-side validation
      if (!customerData) {
        throw new Error('Customer data is required');
      }

      if (typeof customerData !== 'object' || Array.isArray(customerData)) {
        throw new Error('Customer data must be an object');
      }

      // Validate required fields
      if (!customerData.full_name || !customerData.full_name.trim()) {
        throw new Error('Full name is required');
      }

      if (!customerData.phone || !customerData.phone.trim()) {
        throw new Error('Phone number is required');
      }

      // Basic email validation if provided
      if (customerData.email && customerData.email.trim()) {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(customerData.email.trim())) {
          throw new Error('Invalid email format');
        }
      }

      // Validate birth_date format if provided
      if (customerData.birth_date) {
        const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
        if (!dateRegex.test(customerData.birth_date)) {
          throw new Error('Birth date must be in YYYY-MM-DD format');
        }
      }

      // Clean and prepare data
      const cleanCustomerData = {
        ...customerData,
        full_name: customerData.full_name.trim(),
        phone: customerData.phone.trim(),
        email: customerData.email ? customerData.email.trim() : null,
        address: customerData.address ? customerData.address.trim() : null,
        notes: customerData.notes ? customerData.notes.trim() : null,
        skin_type: customerData.skin_type ? customerData.skin_type.trim() : null,
        allergies: customerData.allergies ? customerData.allergies.trim() : null
      };

      return await this._retryRequest(async () => {
        const url = `${FIREBASE_FUNCTIONS_BASE_URL}/customers`;

        console.log('API: Making POST request to create customer:', url);

        // Make the API call
        const response = await fetch(url, {
          method: 'POST',
          headers: await createAuthHeaders(),
          body: JSON.stringify({
            operation: 'create',
            customerData: cleanCustomerData
          })
        });

        // Check if response is ok
        if (!response.ok) {
          await this._handleAPIError(response, 'create');
        }

        // Parse response
        const result = await response.json();

        // Check for API-level errors
        if (!result.success) {
          throw new Error(result.error || 'API request was not successful');
        }

        // Validate response data
        if (!result.data || !result.data.id) {
          throw new Error('Invalid response: missing customer data or ID');
        }

        console.log('API: Successfully created customer', {
          customerId: result.data.id,
          fullName: result.data.full_name,
          phone: result.data.phone,
          email: result.data.email,
          createdAt: result.data.created_at
        });

        return result.data;
      });

    } catch (error) {
      console.error('API Error in create customer:', {
        customerData: {
          full_name: customerData?.full_name,
          phone: customerData?.phone,
          email: customerData?.email
        },
        error: error.message,
        stack: error.stack
      });

      // Re-throw with more context
      throw new Error(`Failed to create customer: ${error.message}`);
    }
  }

  /**
   * Update existing customer
   * @param {string} customerId - Customer ID (UUID)
   * @param {Object} updates - Fields to update
   * @returns {Promise<Object>} Updated customer
   */
  static async update(customerId, updates) {
    try {
      console.log('API: Updating customer', {
        customerId,
        updateFields: Object.keys(updates || {}),
        hasPhone: !!(updates?.phone),
        hasEmail: !!(updates?.email)
      });

      // Critical parameter validation
      if (!customerId) {
        throw new Error('Customer ID is required');
      }

      if (typeof customerId !== 'string') {
        throw new Error('Customer ID must be a string');
      }

      // Validate UUID format
      const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
      if (!uuidRegex.test(customerId)) {
        throw new Error('Invalid customer ID format');
      }

      if (!updates) {
        throw new Error('Updates object is required');
      }

      if (typeof updates !== 'object' || Array.isArray(updates) || updates === null) {
        throw new Error('Updates must be a valid object');
      }

      if (Object.keys(updates).length === 0) {
        throw new Error('At least one field must be provided for update');
      }

      // Client-side validation for critical fields
      if (updates.hasOwnProperty('full_name')) {
        if (!updates.full_name || !updates.full_name.trim()) {
          throw new Error('Full name cannot be empty');
        }
      }

      if (updates.hasOwnProperty('phone')) {
        if (!updates.phone || !updates.phone.trim()) {
          throw new Error('Phone number cannot be empty');
        }
      }

      if (updates.hasOwnProperty('email') && updates.email && updates.email.trim()) {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(updates.email.trim())) {
          throw new Error('Invalid email format');
        }
      }

      if (updates.hasOwnProperty('birth_date') && updates.birth_date && updates.birth_date.trim()) {
        const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
        if (!dateRegex.test(updates.birth_date)) {
          throw new Error('Birth date must be in YYYY-MM-DD format');
        }
      }

      // Clean and prepare update data (preserve null values for field clearing)
      const cleanUpdates = {};

      if (updates.hasOwnProperty('full_name')) {
        cleanUpdates.full_name = updates.full_name ? updates.full_name.trim() : null;
      }

      if (updates.hasOwnProperty('phone')) {
        cleanUpdates.phone = updates.phone ? updates.phone.trim() : null;
      }

      if (updates.hasOwnProperty('email')) {
        cleanUpdates.email = updates.email ? updates.email.trim() : null;
      }

      if (updates.hasOwnProperty('gender')) {
        cleanUpdates.gender = updates.gender;
      }

      if (updates.hasOwnProperty('birth_date')) {
        cleanUpdates.birth_date = updates.birth_date && updates.birth_date.trim() ? updates.birth_date : null;
      }

      if (updates.hasOwnProperty('address')) {
        cleanUpdates.address = updates.address ? updates.address.trim() : null;
      }

      if (updates.hasOwnProperty('notes')) {
        cleanUpdates.notes = updates.notes ? updates.notes.trim() : null;
      }

      if (updates.hasOwnProperty('skin_type')) {
        cleanUpdates.skin_type = updates.skin_type ? updates.skin_type.trim() : null;
      }

      if (updates.hasOwnProperty('allergies')) {
        cleanUpdates.allergies = updates.allergies ? updates.allergies.trim() : null;
      }

      if (updates.hasOwnProperty('preferred_contact')) {
        cleanUpdates.preferred_contact = updates.preferred_contact;
      }

      return await this._retryRequest(async () => {
        const url = new URL(`${FIREBASE_FUNCTIONS_BASE_URL}/customers`);
        url.searchParams.set('customerId', customerId);

        console.log('API: Making POST request to update customer:', url.toString());

        // Make the API call
        const response = await fetch(url.toString(), {
          method: 'POST',
          headers: await createAuthHeaders(),
          body: JSON.stringify({
            operation: 'update',
            updates: cleanUpdates
          })
        });

        // Check if response is ok
        if (!response.ok) {
          await this._handleAPIError(response, 'update');
        }

        // Parse response
        const result = await response.json();

        // Check for API-level errors
        if (!result.success) {
          throw new Error(result.error || 'API request was not successful');
        }

        // Validate response data
        if (!result.data || !result.data.id) {
          throw new Error('Invalid response: missing customer data or ID');
        }

        // Verify the returned customer ID matches the request
        if (result.data.id !== customerId) {
          throw new Error('Response customer ID does not match request ID');
        }

        console.log('API: Successfully updated customer', {
          customerId: result.data.id,
          fullName: result.data.full_name,
          fieldsUpdated: Object.keys(cleanUpdates),
          updatedAt: result.data.updated_at
        });

        return result.data;
      });

    } catch (error) {
      console.error('API Error in update customer:', {
        customerId,
        updateFields: Object.keys(updates || {}),
        error: error.message,
        stack: error.stack
      });

      // Re-throw with more context
      throw new Error(`Failed to update customer: ${error.message}`);
    }
  }

  /**
   * Delete customer - DESTRUCTIVE OPERATION - Requires explicit confirmation
   * @param {string} customerId - Customer ID (UUID)
   * @param {Object} options - Deletion options
   * @param {boolean} options.confirmDeletion - Must be true to proceed
   * @returns {Promise<Object>} Deletion result
   */
  static async delete(customerId, options = {}) {
    try {
      console.warn('API: CRITICAL - Customer deletion requested', {
        customerId,
        hasConfirmation: !!options.confirmDeletion
      });

      // CRITICAL parameter validation
      if (!customerId) {
        throw new Error('Customer ID is required for deletion');
      }

      if (typeof customerId !== 'string') {
        throw new Error('Customer ID must be a string');
      }

      // Validate UUID format
      const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
      if (!uuidRegex.test(customerId)) {
        throw new Error('Invalid customer ID format - deletion aborted for safety');
      }

      // CRITICAL: Require explicit confirmation to prevent accidental deletions
      if (!options.confirmDeletion) {
        throw new Error('Deletion confirmation required - set confirmDeletion: true in options');
      }

      if (options.confirmDeletion !== true) {
        throw new Error('Deletion confirmation must be exactly true - other values not accepted');
      }

      console.warn('API: PROCEEDING with customer deletion after validation', {
        customerId,
        timestamp: new Date().toISOString()
      });

      return await this._retryRequest(async () => {
        const url = new URL(`${FIREBASE_FUNCTIONS_BASE_URL}/customers`);
        url.searchParams.set('customerId', customerId);

        console.warn('API: Making DELETE request (via POST):', url.toString());

        // Make the API call - using POST for safety with delete operation in body
        const response = await fetch(url.toString(), {
          method: 'POST',
          headers: await createAuthHeaders(),
          body: JSON.stringify({
            operation: 'delete',
            confirmDeletion: true // Pass confirmation to server
          })
        });

        // Check if response is ok
        if (!response.ok) {
          await this._handleAPIError(response, 'delete');
        }

        // Parse response
        const result = await response.json();

        // Check for API-level errors
        if (!result.success) {
          throw new Error(result.error || 'Deletion request was not successful');
        }

        // Validate response data
        if (!result.data || !result.data.deletedCustomerId) {
          throw new Error('Invalid deletion response: missing deletion confirmation data');
        }

        // Verify the deleted customer ID matches the request
        if (result.data.deletedCustomerId !== customerId) {
          throw new Error('Response customer ID does not match deletion request ID');
        }

        console.warn('API: Customer deletion completed successfully', {
          deletedCustomerId: result.data.deletedCustomerId,
          customerName: result.data.deletedCustomer?.full_name,
          deletionTimestamp: result.data.deletionTimestamp
        });

        return result.data;
      });

    } catch (error) {
      console.error('API Error in delete customer:', {
        customerId,
        error: error.message,
        stack: error.stack,
        timestamp: new Date().toISOString()
      });

      // Re-throw with more context
      throw new Error(`Failed to delete customer: ${error.message}`);
    }
  }

  /**
   * Get customer statistics including appointments and spending data
   * @param {string} customerId - Customer ID (UUID)
   * @returns {Promise<Object|null>} Customer statistics object or null if not found
   */
  static async getCustomerStats(customerId) {
    try {
      console.log('API: Getting customer statistics', { customerId });

      // Validate parameters
      if (!customerId) {
        throw new Error('Customer ID is required');
      }

      if (typeof customerId !== 'string') {
        throw new Error('Customer ID must be a string');
      }

      // Validate UUID format
      const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
      if (!uuidRegex.test(customerId)) {
        throw new Error('Invalid customer ID format');
      }

      return await this._retryRequest(async () => {
        const url = new URL(`${FIREBASE_FUNCTIONS_BASE_URL}/customers`);
        url.searchParams.set('operation', 'getCustomerStats');
        url.searchParams.set('customerId', customerId);

        console.log('API: Making request to:', url.toString());

        const response = await fetch(url.toString(), {
          method: 'GET',
          headers: await createAuthHeaders(),
        });

        if (!response.ok) {
          await this._handleAPIError(response, 'getCustomerStats');
        }

        const result = await response.json();

        if (!result.success) {
          throw new Error(result.error || 'API request was not successful');
        }

        // Handle null result (customer not found)
        if (result.data === null) {
          console.log('API: Customer stats not found', { customerId });
          return null;
        }

        // Validate response structure
        if (!result.data || typeof result.data !== 'object') {
          throw new Error('Invalid response: missing or invalid stats data');
        }

        // Validate required fields in response
        const requiredFields = ['id', 'full_name', 'total_appointments', 'completed_appointments', 'total_spent'];
        const missingFields = requiredFields.filter(field => !(field in result.data));

        if (missingFields.length > 0) {
          throw new Error(`Invalid response: missing required fields: ${missingFields.join(', ')}`);
        }

        console.log('API: Successfully retrieved customer statistics', {
          customerId: result.data.id,
          fullName: result.data.full_name,
          totalAppointments: result.data.total_appointments,
          completedAppointments: result.data.completed_appointments,
          totalSpent: result.data.total_spent,
          formattedTotalSpent: result.data.formatted_total_spent,
          loyaltyTier: result.data.loyalty_tier,
          completionRate: result.data.completion_rate,
          lastAppointment: result.data.formatted_last_appointment,
          customerSince: result.data.formatted_customer_since,
          isActive: result.data.is_active,
          isFrequent: result.data.is_frequent,
          isHighValue: result.data.is_high_value
        });

        return result.data;
      });

    } catch (error) {
      console.error('API Error in getCustomerStats:', {
        customerId,
        error: error.message,
        stack: error.stack
      });

      throw new Error(`Failed to get customer statistics: ${error.message}`);
    }
  }

  /**
   * Import customers from CSV data
   * @param {Array} customersData - Array of customer objects to import
   * @returns {Promise<Object>} Import results with success/failure counts
   */
  static async importCustomers(customersData) {
    try {
      console.log('API: Importing customers', {
        count: customersData?.length || 0
      });

      // Validate input
      if (!customersData) {
        throw new Error('Customers data is required');
      }

      if (!Array.isArray(customersData)) {
        throw new Error('Customers data must be an array');
      }

      if (customersData.length === 0) {
        throw new Error('No customers data provided for import');
      }

      if (customersData.length > 500) {
        throw new Error('Maximum 500 customers can be imported at once');
      }

      return await this._retryRequest(async () => {
        const url = `${FIREBASE_FUNCTIONS_BASE_URL}/customers`;

        console.log('API: Making POST request to import customers:', url);

        const response = await fetch(url, {
          method: 'POST',
          headers: await createAuthHeaders(),
          body: JSON.stringify({
            operation: 'import',
            customersData: customersData
          })
        });

        if (!response.ok) {
          await this._handleAPIError(response, 'import');
        }

        const result = await response.json();

        if (!result.success) {
          throw new Error(result.error || 'Import request was not successful');
        }

        console.log('API: Successfully imported customers', {
          total: result.data.total,
          successful: result.data.successful,
          failed: result.data.failed,
          skipped: result.data.skipped,
          errorCount: result.data.errors?.length || 0
        });

        return result.data;
      });

    } catch (error) {
      console.error('API Error in importCustomers:', {
        count: customersData?.length || 0,
        error: error.message,
        stack: error.stack
      });

      throw new Error(`Failed to import customers: ${error.message}`);
    }
  }

  /**
   * Export all customers
   * @returns {Promise<Object>} Export data with customers array
   */
  static async exportCustomers() {
    try {
      console.log('API: Exporting customers');

      return await this._retryRequest(async () => {
        const url = `${FIREBASE_FUNCTIONS_BASE_URL}/customers`;

        console.log('API: Making POST request to export customers:', url);

        const response = await fetch(url, {
          method: 'POST',
          headers: await createAuthHeaders(),
          body: JSON.stringify({
            operation: 'export'
          })
        });

        if (!response.ok) {
          await this._handleAPIError(response, 'export');
        }

        const result = await response.json();

        if (!result.success) {
          throw new Error(result.error || 'Export request was not successful');
        }

        console.log('API: Successfully exported customers', {
          count: result.data.customers?.length || 0,
          exportedAt: result.data.exportedAt
        });

        return result.data;
      });

    } catch (error) {
      console.error('API Error in exportCustomers:', {
        error: error.message,
        stack: error.stack
      });

      throw new Error(`Failed to export customers: ${error.message}`);
    }
  }
}

export default CustomerAPI;